import { Fragment, type ReactNode } from "react";

/**
 * Matches bare `http(s)://` and `www.` URLs, greedily up to the next whitespace / `<`. Global so it
 * can be stepped with `exec`. A boundary check on the preceding character (below) — rather than a
 * lookbehind, which isn't safe across all browser targets — keeps it from matching inside emails /
 * handles like `author@www.brand.com`. Trailing punctuation is trimmed by
 * {@link splitTrailingUrlPunctuation} so a closing paren that belongs to the URL is preserved.
 */
const URL_REGEX = /(?:https?:\/\/|www\.)[^\s<]+/gi;

/** A URL only starts at a boundary — not immediately after an email/handle/identifier character. */
const NON_BOUNDARY_BEFORE = /[\w@]/;

const DEFAULT_LINK_CLASS =
  "underline underline-offset-4 hover:text-primary break-words";

const TRAILING_PUNCTUATION = ".,;:!?]}'\"";

/**
 * Splits a greedily-matched URL into `[url, trailingText]`, trimming trailing sentence punctuation
 * and UNMATCHED closing parens while keeping a `)` that balances a `(` inside the URL.
 *
 * Without the balance check, `https://en.wikipedia.org/wiki/Function_(mathematics).` would lose its
 * closing paren and 404; with it, the paren stays in the link and only the sentence period is
 * trimmed back out to plain text.
 */
function splitTrailingUrlPunctuation(url: string): [string, string] {
  let end = url.length;
  while (end > 0) {
    const char = url[end - 1];
    if (char === ")") {
      const head = url.slice(0, end);
      const opens = (head.match(/\(/g) ?? []).length;
      const closes = (head.match(/\)/g) ?? []).length;
      if (closes <= opens) break; // balanced — this paren belongs to the URL
    } else if (!TRAILING_PUNCTUATION.includes(char)) {
      break;
    }
    end -= 1;
  }
  return [url.slice(0, end), url.slice(end)];
}

export interface LinkifiedTextProps {
  /** Free text that may contain bare URLs. Rendered verbatim when it has none. */
  text: string | null | undefined;
  /** Optional override for the class applied to each rendered anchor. */
  linkClassName?: string;
}

/**
 * Renders free text with bare URLs turned into clickable links.
 *
 * Deliberately NOT a markdown/HTML parser: plain text is passed through verbatim (React-escaped);
 * only `http(s)://` / `www.` URLs become `<a>` tags (new tab, `rel="noopener noreferrer"`). Brands
 * paste links into brief guidelines as plain text and the Creator Portal previously rendered them
 * inert — creators had to copy/paste them into the address bar (EPD-10400).
 */
export function LinkifiedText({ text, linkClassName }: LinkifiedTextProps) {
  if (!text) return null;

  const nodes: ReactNode[] = [];
  // Fresh regex per call — a shared global regex would carry `lastIndex` across renders.
  const regex = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  let cursor = 0; // start of the not-yet-emitted text
  let match: RegExpExecArray | null;

  // Keys are the match/segment START OFFSET in the source string — a real, stable identity that is
  // unique across the render (positions never collide) and never the array index.
  const pushText = (value: string, start: number) => {
    if (value) nodes.push(<Fragment key={`t${start}`}>{value}</Fragment>);
  };

  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const before = start > 0 ? text[start - 1] : "";
    const candidate = match[0];
    // A scheme-less `www.` match whose authority (host, before the first `/`, `?` or `#`) contains
    // `@` is an email, not a URL — e.g. `www.brand.com@example.com`, or `x.www.y@example.com` where
    // the preceding-char check can't help. URLs whose only `@` is in the PATH — YouTube handle links
    // like `www.youtube.com/@name` — keep an `@`-free authority, so they still linkify.
    const isEmailLikeWww =
      /^www\./i.test(candidate) && candidate.split(/[/?#]/, 1)[0].includes("@");
    if (NON_BOUNDARY_BEFORE.test(before) || isEmailLikeWww) {
      // Inside an email/handle (e.g. `author@www.brand.com`) — not a real URL start. Leave it in the
      // surrounding text (cursor unchanged); the region is emitted with the next accepted match/tail.
      continue;
    }

    pushText(text.slice(cursor, start), cursor);

    const [url, trailing] = splitTrailingUrlPunctuation(candidate);
    const href = /^www\./i.test(url) ? `https://${url}` : url;
    nodes.push(
      <a
        key={`l${start}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName ?? DEFAULT_LINK_CLASS}
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(<Fragment key={`p${start}`}>{trailing}</Fragment>);
    cursor = start + candidate.length;
  }

  pushText(text.slice(cursor), cursor);

  return <>{nodes}</>;
}
